<template>
  <v-card flat :loading="loading" class="card">
    <div>

      <SelectedCourse
        :appId="appId"
        :routeName="changeScopeRouteName"
        @update:course="setCourse"
      />
    </div>

    <v-card-title v-if="!loading"
    >{{ $t("Subject") }}
      {{ subject.name ? subject.name : "..." }}</v-card-title
    >

    <v-card-text v-if="!loading && !error">
      <v-form class="px-5 mx-5" ref="form" v-model="validForm">
        <div class="flex-lg-row flex-column">
          <v-row>
            <v-col lg="4" md="12" sm="12">
              <v-text-field
                id="subjectAddEdit-txt-name"
                :rules="nameRules"
                :label="$t('Title')"
                v-model.trim="editSubject.name"
              />
            </v-col>
            <v-col lg="4" md="12" sm="12">
              <v-text-field
                id="subjectAddEdit-txt-lecturesCount"
                type="number"
                :label="$t('lectureCount')"
                v-model="editSubject.lectureCount"
                :rules="lectureCountRules"
              />
            </v-col>
            <v-col lg="4" md="12" sm="12">
              <v-select
                :disabled="!courseActive"
                :items="activeStatus"
                item-title="text"
                item-value="value"
                :menu-props="{ location: 'bottom', offset: 10 }"
                :label="$t('Active')"
                :hint="!courseActive ? $t('courseEditDisabled') : ''"
                v-model="editSubject.active"
                persistent-hint
              />
            </v-col>

            <v-alert
              v-if="resError"
              variant="outlined"
              color="warning"
              class="text-center d-lg-block d-none mx-1"
            >{{ $utils.servertranslate(resError) }}</v-alert
            >
          </v-row>
          <v-row>
            <v-col col="1">
              <h3 class="mb-5">{{ $t('ILOS') }}</h3>
              <div v-if="!isFlexibleILOs">
                <ilos-edit :ilos="courseILOS" :selectedIlos="editSubject.ilos as HeaderILos" />
              </div>
              <div v-else>
                <SubjectFlexibleILOs
                  :items="flexibleILOs"
                  @update-flexible-ilos="updateFlexibleILOs"
                />
              </div>
            </v-col>
          </v-row>
        </div>
        <v-card-actions class="pt-5 mt-5">
          <v-btn
            color="primary"
            variant="flat"
            class="d-lg-block d-none mx-1"
            @click="onSaveClick"
            id="btn-subject-edit-save"
          >{{ $t("buttons.save") }}</v-btn
          >

          <v-btn
            class="d-lg-block d-none mx-1 blue-grey white--text"
            id="btn-subject-edit-back"
            @click="onBackClick"
          >{{ $t("buttons.back") }}</v-btn
          >
        </v-card-actions>
      </v-form>
    </v-card-text>
    <v-alert v-if="error" variant="outlined" color="warning" class="text-center">{{
      $utils.servertranslate(error)
    }}</v-alert>

    <v-alert
      v-if="resError"
      variant="outlined"
      color="warning"
      class="d-lg-none mx-1"
    >{{ $utils.servertranslate(resError) }}</v-alert
    >

    <v-card-actions v-if="!loading">
      <v-btn
        color="primary"
        variant="flat"
        class="d-lg-none flex-md-grow-0 flex-grow-1 mx-1"
        @click="onSaveClick"
        id="btn-subject-edit-save"
      >{{ $t("buttons.save") }}</v-btn
      >

      <v-btn
        class="d-lg-none flex-md-grow-0 flex-grow-1 mx-1 blue-grey white--text"
        id="btn-subject-edit-back"
        @click="onBackClick"
      >{{ $t("buttons.back") }}</v-btn
      >
    </v-card-actions>
  </v-card>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import IlosComponets from '@/shared/ilos-add-component.vue';
import ScopeComponent from '@/components/scope.component.vue';
import { useToaster } from '@/composables/useToaster';
import SubjectProvider from '../../providers/subject.provider';

import { Subject } from '../../types/subject.types';
import { Lang } from '../../types/general.type';
import { SUBJECT_ROUTE_NAME, APPIDS } from '../../enums';
import { HeaderILos, FlexibleILOs } from '../../types/ilos.type';
import SelectedCourse from '../SelectedCourse.component.vue';
import { CourseBasic } from '../../types/course.type';
import SubjectFlexibleILOs from './SubjectFlexibleILOs.component.vue';

export default defineComponent({
  name: 'SubjectAddEdit',

  setup() {
    return {
      ...useToaster(),
    };
  },

  components: {
    'ilos-edit': IlosComponets,
    SelectedCourse,
    ScopeComponent,
    SubjectFlexibleILOs,
  },

  data() {
    return {
      appId: APPIDS.SUBJECTS,
      changeScopeRouteName: SUBJECT_ROUTE_NAME.COURSE_LIST,

      courseId: this.$route?.params.courseId as string,
      subjectId: this.$route?.params.subjectId as string,

      courseActive: false,

      subject: {} as Subject,
      loading: false as boolean | string,

      editSubject: {
        name: '',
        lectureCount: '',
      } as Subject,

      validForm: true,

      nameRules: [
        (v: string) => v.trim().length > 0 || this.$t('Required'),
        (v: string) => v.length <= 50 || this.$t('validations.maxLength', { max: 50 }),
      ],

      lectureCountRules: [
        (v: number) => !v || Number(v) >= 0 || this.$t('input_number_validation'),
        (v: number) => Number(v) <= 50 || this.$t('validations.maxValue', { max: 50 }),
      ],

      courseILOS: {} as HeaderILos,
      isFlexibleILOs: false,
      flexibleILOs: {} as FlexibleILOs,

      error: null as Lang | null,
      resError: null as Lang | null,

      course: {} as CourseBasic,
    };
  },

  computed: {
    activeStatus() {
      return [
        { text: this.$t('Yes'), value: true },
        { text: this.$t('No'), value: false },
      ];
    },
  },

  mounted() {
    if (this.$route.name === SUBJECT_ROUTE_NAME.EDIT) {
      this.getSubjectData(this.subjectId);
    } else if (this.$route.name === SUBJECT_ROUTE_NAME.ADD) {
      this.creatNewSubject();
    }
  },

  methods: {
    updateFlexibleILOs(ilos: FlexibleILOs) {
      this.flexibleILOs = JSON.parse(JSON.stringify(ilos));
    },

    async getSubjectData(subjectId: string) {
      try {
        this.showLoading();

        const { subject, ilos } = await SubjectProvider.getSubjectInfo(
          subjectId,
          this.courseId,
        );

        this.subject = subject;

        if (subject.isFlexibleILOs) {
          this.flexibleILOs = ilos as FlexibleILOs;
          this.isFlexibleILOs = !!subject.isFlexibleILOs;
        } else {
          this.courseILOS = ilos as HeaderILos;
        }

        Object.assign(this.editSubject, this.subject);
        this.hideLoading();
      } catch (err) {
        this.hideLoading();
        this.error = err as Lang;
      }
    },

    async creatNewSubject() {
      this.subject = this.editSubject;
      this.editSubject.active = true;

      // default ILOs structure
      this.editSubject.ilos = {
        '1.1.': [],
        '1.2.': [],
        '1.3.': [],
        '1.4.': [],
      };

      try {
        this.showLoading();

        const { ilos, isFlexibleILOs } = await SubjectProvider.getCourseIlos(
          this.appId,
          this.courseId,
        );

        if (isFlexibleILOs) {
          this.flexibleILOs = ilos as FlexibleILOs;
          this.isFlexibleILOs = isFlexibleILOs;
        } else {
          this.courseILOS = ilos as HeaderILos;
        }

        this.hideLoading();
      } catch (err) {
        this.hideLoading();
        this.error = err as Lang;
      }
    },

    showLoading() {
      this.loading = 'secondary';
    },

    hideLoading() {
      this.loading = false;
    },

    async onSaveClick() {
      this.resError = null;

      if (!(this.$refs.form as HTMLFormElement).validate()) return;

      if (this.$route.name === SUBJECT_ROUTE_NAME.EDIT) {
        await this.updateSubject(this.editSubject);
      } else if (this.$route.name === SUBJECT_ROUTE_NAME.ADD) {
        await this.addSubject(this.editSubject);
      }
    },

    async updateSubject(subject: Subject) {
      try {
        const subjectObj = { ...subject };

        if (this.isFlexibleILOs) {
          subjectObj.ilos = this.flexibleILOs;
        }

        await SubjectProvider.updateSubject(
          subjectObj,
          this.courseId,
          this.isFlexibleILOs,
        );

        this.$router.go(-1);
      } catch (err) {
        this.hideLoading();
        this.resError = err as Lang;
      }
    },

    async addSubject(subject: Subject) {
      try {
        const subjectObj = { ...subject };

        if (this.isFlexibleILOs) {
          subjectObj.ilos = this.flexibleILOs;
        }

        const subjectId = await SubjectProvider.addSubject(
          subjectObj,
          this.courseId,
          this.isFlexibleILOs,
        );

        this.$router.replace({
          name: 'subject-list',
          params: { courseId: this.courseId },
          hash: `#${subjectId}`,
        });
      } catch (err) {
        this.hideLoading();
        this.resError = err as Lang;
      }
    },

    onBackClick() {
      this.$router.go(-1);
    },

    setCourse(course: CourseBasic) {
      this.course = course;
      this.courseActive = !!course.active;
    },
  },
});
</script>

<style scoped>
.card {
  min-height: 10px;
}
</style>
